import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { Flex, Form } from 'ui';
import type { ChatroomsSearchParamsOptions } from '../types';
import { ChatroomSidebarFilters, type ChatroomSidebarFiltersFormValues } from './chatroom-sidebar-filters';
import { type ChatroomSidebarContentFilter, useGetChatroomSidebarContentFilter, useGetInitialValues } from './hooks';

type FormValues = ChatroomSidebarFiltersFormValues;

type P = {
  children: (({ filter }: { filter: ChatroomSidebarContentFilter }) => ReactNode) | ReactNode;
  onCreateChatClicked: () => Promise<void>;
  searchParamsOptions: ChatroomsSearchParamsOptions;
};

const ChatroomSidebar = ({ children, onCreateChatClicked, searchParamsOptions: { filter, updateSearchParams } }: P) => {
  const [form] = Form.useForm<FormValues>();
  const search = Form.useWatch('search', form);
  const type = Form.useWatch('type', form);

  const initialValues = useGetInitialValues(filter);
  const getChatroomSidebarContentFilter = useGetChatroomSidebarContentFilter();

  const onValuesChange = useCallback(
    (_: Partial<FormValues>, values: FormValues) => {
      updateSearchParams({ filter: values });
    },
    [updateSearchParams],
  );

  return (
    <Flex className="chat-sidebar" vertical>
      <Form<FormValues>
        className="chat-sidebar__form"
        form={form}
        initialValues={initialValues}
        noOptional
        onValuesChange={onValuesChange}
        resetOnHistoryChange
      >
        <ChatroomSidebarFilters onCreateChatClicked={onCreateChatClicked} />
        <div className="chat-sidebar__content">
          {typeof children === 'function'
            ? children({ filter: getChatroomSidebarContentFilter(search, type) })
            : children}
        </div>
      </Form>
    </Flex>
  );
};

export default ChatroomSidebar;
